import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { DAILY_BONUS_MONEY, formatMoney, normaliseLook, starterLook } from '@kopi-kaki/protocol';
import type { Gender, SelfProfile } from '@kopi-kaki/protocol';
import { AVATAR_HEIGHT, AVATAR_WIDTH } from '../game/avatarArt.js';
import { api, errorMessage } from '../lib/api.js';
import { setSelf } from '../state/store.js';
import { AvatarSprite, PixelIcon } from './components.js';
import { PasswordField, TermsCheckbox } from './Legal.js';

type Tab = 'login' | 'register';

// Written in the original three-field format; normaliseLook upgrades them.
const HERO_CAST = [
  { look: normaliseLook({ body: 1, shirt: 0, hair: 0 }), action: 'dance', direction: 3 },
  { look: normaliseLook({ body: 4, shirt: 3, hair: 5 }), action: 'wave', direction: 4 },
  { look: normaliseLook({ body: 0, shirt: 6, hair: 6 }), action: 'dance', direction: 2 },
  { look: normaliseLook({ body: 3, shirt: 4, hair: 3 }), action: 'stand', direction: 3 },
  { look: normaliseLook({ body: 6, shirt: 1, hair: 2 }), action: 'dance', direction: 4 },
] as const;

/**
 * Integer scale for the hero avatars, so the pixel art stays crisp: 3x on
 * roomy screens, 2x on phones and short landscape screens.
 */
function useHeroScale(): number {
  const query = '(max-width: 520px), (max-height: 560px)';
  const [small, setSmall] = useState(() => window.matchMedia?.(query).matches ?? false);
  useEffect(() => {
    const media = window.matchMedia?.(query);
    if (!media) return;
    const onChange = () => setSmall(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);
  return small ? 2 : 3;
}

/** True on phones and tablets, where the game is playable but a computer is nicer. */
function useIsTouchDevice(): boolean {
  return window.matchMedia?.('(pointer: coarse)').matches ?? false;
}

function usePrefersReducedMotion(): boolean {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

export function LoginScreen() {
  const [tab, setTab] = useState<Tab>('login');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scale = useHeroScale();
  const still = usePrefersReducedMotion();
  const touch = useIsTouchDevice();

  const run = async (action: () => Promise<{ user: SelfProfile }>) => {
    setBusy(true);
    setError(null);
    try {
      const { user } = await action();
      setSelf(user);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="landing">
      <div className="landing-sky" aria-hidden />
      <section className="landing-hero">
        <h1 className="logo">
          <span className="logo-kopi">KOPI</span>
          <span className="logo-kaki">KAKI</span>
        </h1>
        <p className="tagline">Hang out. Sip kopi. Make kakis.</p>
        <p className="beta-note">
          <b>BETA</b> We are still building the game, so expect a few bugs.{' '}
          <a href="https://kopikaki.app/volunteer/" target="_blank" rel="noopener">
            Help shape it: volunteer as a game master or moderator
          </a>
        </p>
        {touch && (
          <p className="device-note">
            Tip: Kopi Kaki is much more fun on a computer, with a bigger view, mouse and keyboard.
          </p>
        )}
        <div className="hero-cast" aria-hidden>
          {HERO_CAST.map((member, i) => (
            // A fixed box: dance frames are trimmed to different heights, and the
            // page below must not shift with them.
            <div
              key={i}
              className="hero-avatar"
              style={{ animationDelay: `${i * 0.15}s`, width: (AVATAR_WIDTH - 2) * scale, height: AVATAR_HEIGHT * scale }}
            >
              <AvatarSprite
                look={member.look}
                action={still ? 'stand' : member.action}
                direction={member.direction}
                scale={scale}
                animate={!still}
              />
            </div>
          ))}
        </div>
      </section>

      <section className="panel landing-card">
        <nav className="tabs" role="tablist">
          {(
            [
              ['login', 'Log in'],
              ['register', 'Sign up'],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              role="tab"
              aria-selected={tab === id}
              className={`tab ${tab === id ? 'tab-active' : ''}`}
              onClick={() => {
                setTab(id);
                setError(null);
              }}
            >
              {label}
            </button>
          ))}
        </nav>

        {tab === 'login' && <LoginForm busy={busy} onSubmit={(u, p) => run(() => api.login(u, p))} />}
        {tab === 'register' && (
          <RegisterForm busy={busy} onSubmit={(u, p, d, g) => run(() => api.register(u, p, g, d || undefined))} />
        )}

        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
      </section>

      <About />
    </main>
  );
}

function LoginForm({ busy, onSubmit }: { busy: boolean; onSubmit: (username: string, password: string) => void }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const submit = (event: FormEvent) => {
    event.preventDefault();
    onSubmit(username.trim(), password);
  };
  return (
    <form className="form" onSubmit={submit}>
      <label>
        Username
        <input
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoComplete="username"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="next"
          required
        />
      </label>
      <PasswordField
        label="Password"
        value={password}
        onChange={setPassword}
        autoComplete="current-password"
        enterKeyHint="go"
      />
      <button className="btn btn-green btn-big" disabled={busy}>
        {busy ? 'Checking...' : 'Log in'}
      </button>
    </form>
  );
}

const GENDER_PREVIEW: Record<Gender, ReturnType<typeof starterLook>> = {
  male: starterLook('male', () => 0),
  female: starterLook('female', () => 0),
};

function GenderPicker({ value, onChange }: { value: Gender | null; onChange: (gender: Gender) => void }) {
  return (
    <fieldset className="gender-picker">
      <legend>
        Gender <small>(sets the avatar you start with)</small>
      </legend>
      {(['male', 'female'] as const).map((gender) => (
        <label key={gender} className={`gender-option ${value === gender ? 'gender-option-on' : ''}`}>
          <input
            type="radio"
            name="gender"
            value={gender}
            checked={value === gender}
            onChange={() => onChange(gender)}
            required
          />
          <AvatarSprite look={GENDER_PREVIEW[gender]} direction={3} scale={2} />
          <span>{gender === 'male' ? 'Male' : 'Female'}</span>
        </label>
      ))}
    </fieldset>
  );
}

export function RegisterForm({
  busy,
  onSubmit,
  submitLabel = 'Create account',
  askGender = true,
}: {
  busy: boolean;
  onSubmit: (username: string, password: string, displayName: string, gender: Gender) => void;
  submitLabel?: string;
  /** A guest upgrading an account keeps the avatar they already have. */
  askGender?: boolean;
}) {
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [gender, setGender] = useState<Gender | null>(null);
  const [accepted, setAccepted] = useState(false);
  const mismatch = confirm.length > 0 && confirm !== password;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (mismatch || !accepted || (askGender && !gender)) return;
    onSubmit(username.trim(), password, displayName.trim(), gender ?? 'male');
  };

  return (
    <form className="form" onSubmit={submit}>
      <label>
        <span>
          Username <small>(for logging in)</small>
        </span>
        <input
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoComplete="username"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="next"
          pattern="[A-Za-z0-9_\-]{3,24}"
          title="3-24 letters, numbers, hyphens or underscores"
          required
        />
      </label>
      <label>
        <span>
          Display name <small>(optional, shown in the game)</small>
        </span>
        <input
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          maxLength={24}
          autoComplete="nickname"
          enterKeyHint="next"
        />
      </label>
      {askGender && <GenderPicker value={gender} onChange={setGender} />}
      <PasswordField
        label="Password"
        hint="(8+ characters)"
        value={password}
        onChange={setPassword}
        autoComplete="new-password"
        minLength={8}
        enterKeyHint="next"
      />
      <PasswordField
        label="Confirm password"
        value={confirm}
        onChange={setConfirm}
        autoComplete="new-password"
        enterKeyHint="done"
        invalid={mismatch}
      />
      {mismatch && <p className="form-error">Passwords do not match.</p>}
      <TermsCheckbox checked={accepted} onChange={setAccepted} />
      <button className="btn btn-green btn-big" disabled={busy || mismatch || !accepted || (askGender && !gender)}>
        {busy ? 'Setting up...' : submitLabel}
      </button>
    </form>
  );
}

const ABOUT: readonly { icon: string; title: string; text: string }[] = [
  {
    icon: 'chat',
    title: 'Hang out',
    text: 'Walk around pixel-art rooms, chat in speech bubbles, wave, dance and sit on the big orange couch. Order kopi, teh tarik and Milo at the counter.',
  },
  {
    icon: 'trophy',
    title: 'Arcade and table games',
    text: 'Beat your mates at Kopi Catch, MRT Dash, Kueh Stack, Chope! and the live Dragon Boat Race. Sit down to tic-tac-toe, Connect Four, checkers, Chinese chess, snakes and ladders, Texas Hold\'em or Singapore mahjong, with Auntie and Uncle bots to fill empty seats.',
  },
  {
    icon: 'coin',
    title: 'Earn Kaki Dollars',
    text: `Collect ${formatMoney(DAILY_BONUS_MONEY)} every day, work as a barista, office clerk, mall cashier or hawker chef, and get promoted. Sell things in the player marketplace or open your own shop. It is play money only.`,
  },
  {
    icon: 'wardrobe',
    title: 'Make it yours',
    text: 'Design your character from head to toe, buy premium clothes, and draw your own pixel shirt prints you can sell to other players.',
  },
  {
    icon: 'rooms',
    title: 'Build a room',
    text: 'Start from a template, then buy and place furniture, including Singapore icons like the Merlion, an HDB block and the dragon playground. Friends can drop by and like your room.',
  },
  {
    icon: 'heart',
    title: 'Adopt a pet',
    text: 'Pick a cat, dog, parrot, rabbit, hamster, tortoise or otter. It follows you around, gets hungry and sleepy, learns tricks and digs for treasure. Pets never die.',
  },
  {
    icon: 'user',
    title: 'Friends and safety',
    text: 'Add friends, see who is online, send messages and room invites. For ages 13 and up, with a word filter, block and report tools, and moderators keeping watch.',
  },
] as const;

const PLACES = [
  'Kopi Kaki Kopitiam',
  'Hawker Centre',
  'Void Deck',
  'Merlion Park',
  'Orchard Kaki Mall',
  'Raffles Place Office Tower',
  'Game Lounge',
  'Pasar Malam Funfair',
  'East Coast Park',
  'Pet Shop @ Tiong Bahru',
  'Bishan Dog Run',
] as const;

/** What the game is, for people deciding whether to sign up. */
function About() {
  return (
    <section className="landing-about" aria-labelledby="about-title">
      <h2 id="about-title">What is Kopi Kaki?</h2>
      <p className="about-lead">
        A free, Singapore-made pixel-art social game in your browser. <em>Kopi kaki</em> is Singlish for &ldquo;coffee
        buddy&rdquo;: the friend you meet for kopi.
      </p>
      <ul className="about-grid">
        {ABOUT.map((item) => (
          <li key={item.title} className="about-card">
            <h3>
              <PixelIcon name={item.icon} size={20} /> {item.title}
            </h3>
            <p>{item.text}</p>
          </li>
        ))}
      </ul>
      <h3 className="about-places-title">Places to go</h3>
      <ul className="about-places">
        {PLACES.map((place) => (
          <li key={place}>{place}</li>
        ))}
      </ul>
    </section>
  );
}
